import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Icon from '~/elements/Icon';
import Modal from '~/components/Modal';
import { VRPAT } from '~/config/constants';

import { GPlay, GPlayLight, AppStore, AppStoreLight } from '~/assets/';

const iconTypes = {
  success: ['fal', 'check-circle'],
  error: ['fal', 'times'],
  warning: ['fal', 'exclamation-triangle'],
};

const bgTypes = theme => ({
  success: theme.colors.primary,
  warning: theme.colors.secondary,
  error: theme.colors.danger,
});

const colorTypes = theme => ({
  success: theme.colors.white,
  warning: theme.colors.institucionalDark,
  error: theme.colors.white,
});

const Desktop = styled.div`
  background: ${({ theme, type }) => bgTypes(theme)[type]};
  color: ${({ theme, type }) => colorTypes(theme)[type]};
  border-radius: 20px;
  text-align: center;
  padding: 57px;

  .icon {
    font-size: 42px;
  }

  .title {
    margin: 16px 0 0;
    display: inline-block;
  }
`;

const Description = styled.div`
  margin: 16px 0 0;
  line-height: 36px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justfy-content: center;
`;

const Presentation = styled.text`
  font-family: 'FuturaBold';
  font-weight: 700;
  font-size: 56px;
  line-height: 103.6px;
  color: ${({ theme }) => theme.colors.white};
`;

const Title = styled.text`
  font-family: 'FuturaBold';
  font-weight: 700;
  font-size: 24px;
  line-height: 40.8px;
  color: ${({ theme }) => theme.colors.white};
`;

const DescriptionJVCEF = styled.div`
  font-size: 18px;
  margin: 16px 0 0;
`;

const AppsContainer = styled.div`
  padding-top: 20px;
`;

const UL = styled.div`
  padding: 0;
`;

const LI = styled.div`
  font-size: 16px;
  margin-top: 10px;
  list-style: none;
  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.white};
  }
`;

const Toast = ({
  type = 'success',
  title,
  description,
  presentation,
  custom,
  store,
  modalProps,
}) => {
  const issuer = useSelector(state => state?.issuer);
  const isVr = issuer?.name === VRPAT;

  return (
    <Modal
      coverAll
      fullWidth
      maxWidth="sm"
      data-testid="toast-desktop"
      {...modalProps}
    >
      <Desktop type={type}>
        <div className="icon">
          <Icon icon={iconTypes[type]} />
        </div>

        {!custom ? (
          <>
            {title && <b className="title">{title}</b>}
            {description && <Description>{description}</Description>}
          </>
        ) : (
          <>
            <TitleContainer>
              {presentation && <Presentation>{presentation}</Presentation>}
              {title && <Title>{title}</Title>}
            </TitleContainer>

            {description && <DescriptionJVCEF>{description}</DescriptionJVCEF>}

            {store && (
              <AppsContainer>
                <UL>
                  <LI>
                    <a
                      href={issuer?.app?.android}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src={!isVr ? GPlayLight : GPlay} alt="" />
                    </a>
                  </LI>
                  <LI>
                    <a href={issuer?.app?.ios} target="_blank" rel="noreferrer">
                      <img src={!isVr ? AppStoreLight : AppStore} alt="" />
                    </a>
                  </LI>
                </UL>
              </AppsContainer>
            )}
          </>
        )}
      </Desktop>
    </Modal>
  );
};

Toast.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  type: PropTypes.oneOf(['success', 'error', 'warning']),
  modalProps: PropTypes.shape({
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    disableBackdropClick: PropTypes.bool,
  }),
};

export default Toast;
